import {
    Avatar,
    Button,
    Divider,
    Flex,
    useStyles,
    useTheme,
} from '@archipro-design/aria';

import { AddLine } from '@archipro-design/icons';
import type { DesignBoardWithDetailedPinsFragment } from 'generated/graphql';
import { getNameInitials } from '~/modules/user/util/get-name-initials';
import * as S from './FilterBarAvatars.style';
import { useDesignBoardCollaboratorsContext } from '@modules/design-board/component/manage/collaborator-overview/context/DesignBoardCollaboratorsContext';
import BoardActionWithLoginPrompt from '@modules/design-board/component/board-detail/BoardActionWithLoginPrompt';
import { useTracker } from '@archipro-website/tracker';
import { SHOW_ADD_BUTTON_AFTER_AVATAR } from '~/modules/design-board/config';

interface FilterBarAvatarsProps {
    board: DesignBoardWithDetailedPinsFragment;
}

const FilterBarAvatars = ({ board }: FilterBarAvatarsProps) => {
    const { css } = useStyles();
    const tracker = useTracker();

    const theme = useTheme();
    const darkOutlinedButtonBorderNormal = `1px solid ${theme.siteVariables.colors.charcoal[250]}`;
    const collaboratorsContext = useDesignBoardCollaboratorsContext();

    const handleClick = () => {
        collaboratorsContext.dispatch({
            type: 'modal-visibility',
            data: {
                visibility: 'visible',
            },
        });
        tracker.log('InviteCollaboratorsReveal', {
            url: new URL(window.location.href),

            data: {
                ElementID:
                    'designboard_detail_header_inviteCollaboratorsReveal',
                Source: 'designBoard, detail',
            },
        });
    };

    const totalCollaborators = board.Collaborators?.length || 0;

    const displayNumber = totalCollaborators > 3 ? 2 : 3;
    const totalAvatar =
        totalCollaborators > 3 ? (
            <BoardAvatar initials={`+${totalCollaborators - 2}`} />
        ) : null;

    return (
        <>
            <Flex
                className={css(S.FilterBarAvatars)}
                column={false}
                onClick={handleClick}
            >
                <BoardAvatar
                    initials={getNameInitials(board.Owner?.Name || '')}
                    image={board.Owner?.MemberImage?.Logo || undefined}
                />
                {totalCollaborators > 0 && <Divider vertical size={1} />}

                <Flex className={css(S.Collaborators)}>
                    {board.Collaborators?.slice()
                        .reverse()
                        .slice(0, displayNumber)
                        .map((collaborator) => (
                            <BoardAvatar
                                initials={getNameInitials(
                                    collaborator.Name ||
                                        collaborator.Email ||
                                        ''
                                )}
                                key={collaborator.ID}
                                image={
                                    collaborator.ProfileImageLink || undefined
                                }
                            />
                        ))}
                    {totalAvatar}
                    {SHOW_ADD_BUTTON_AFTER_AVATAR &&
                        board.Permission.CanAddCollaborators && (
                            <BoardActionWithLoginPrompt
                                action={
                                    <Button
                                        color="dark"
                                        iconBefore={<AddLine />}
                                        size={24}
                                        variables={{
                                            buttonPadding: 16,
                                            iconPadding: 4,
                                            darkOutlinedButtonBorderNormal,
                                            buttonSize24IconXMargin: '0',
                                        }}
                                        variant="outlined"
                                        className={css(S.InviteButton)}
                                        onClick={handleClick}
                                    ></Button>
                                }
                            />
                        )}
                </Flex>
            </Flex>
        </>
    );
};

const BoardAvatar = ({
    initials,
    image,
}: {
    initials: string;
    image?: string;
}) => {
    return (
        <Avatar
            initials={initials}
            size={40}
            imageFit={'contain'}
            image={image}
            className={image ? 'has-image' : 'no-image'}
        />
    );
};

export default FilterBarAvatars;
